import { EllipsedText, isValidLatitude, isValidLongitude, useMoment } from '@gorila-shared-ui/components';
import { StyledLink } from 'baseui/link';
import { StyledCell, StyledRow } from 'baseui/table';
import { CSSProperties, memo } from 'react';
import { areEqual, ListChildComponentProps } from 'react-window';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  TELEMETRY_ADDRESS_COLUMN_SIZE,
  TELEMETRY_DATE_COLUMN_MAX_WIDTH,
  TELEMETRY_DATE_COLUMN_SIZE,
  TELEMETRY_IDENT_COLUMN_SIZE,
  TELEMETRY_LABEL_COLUMN_SIZE,
  TELEMETRY_OPERATIONAL_STATE_COLUMN_SIZE,
  TELEMETRY_RPM_COLUMN_SIZE,
  TELEMETRY_SPEED_COLUMN_MAX_WIDTH,
  TELEMETRY_SPEED_COLUMN_SIZE,
} from '../../../constants/reports';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { temporaryPositionMarkerState } from '../../../storage/map';
import { selectedReportRowState } from '../../../storage/reports';
import { HistoricTelemetry } from '../../../types/currentState';
import { isDevice } from '../../../utils/currentStates';

function TelemetryReportRow({
  data,
  index,
  style,
}: Readonly<
  ListChildComponentProps<{
    states: HistoricTelemetry[];
    extended?: boolean;
  }>
>) {
  const state = data.states[index];
  const extended = data.extended;
  const [selected, setSelected] = useRecoilState(selectedReportRowState);
  const { theme, css, styles } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { zoomTo } = useCamera();
  const setTempPosition = useSetRecoilState(temporaryPositionMarkerState);

  const onClick = () => {
    const { position_latitude: lat, position_longitude: lng } = state;
    setSelected(index);
    if (lat && lng && isValidLatitude(lat) && isValidLongitude(lng)) {
      zoomTo({ lat, lng }, 18);
      if (extended) {
        setTempPosition({ address: '', point: { lat, lng } });
      }
    }
  };

  return (
    <StyledRow
      key={index}
      style={{ ...(style as CSSProperties), cursor: 'pointer' }}
      className={css({
        backgroundColor:
          selected === index
            ? theme.colors.gray100 // Active color
            : (index + 1) % 2 === 0
              ? theme.colors.tableStripedBackground
              : '',
        ':hover': {
          background: theme.colors.buttonTertiaryHover,
        },
      })}
      onClick={onClick}
    >
      {extended && (
        <StyledCell
          style={{
            flex: TELEMETRY_IDENT_COLUMN_SIZE,
            ...styles.smallTableCell,
          }}
        >
          {isDevice(state.asset.device) ? state.asset.device.ident : '-'}
        </StyledCell>
      )}
      {extended && (
        <StyledCell
          style={{
            flex: TELEMETRY_LABEL_COLUMN_SIZE,
            ...styles.smallTableCell,
          }}
        >
          {state.asset.label || '-'}
        </StyledCell>
      )}
      <StyledCell
        style={{
          flex: TELEMETRY_DATE_COLUMN_SIZE,
          maxWidth: TELEMETRY_DATE_COLUMN_MAX_WIDTH,
          ...styles.smallTableCell,
        }}
      >
        {formatDateFromTs(+state.timestamp)}
      </StyledCell>
      <StyledCell
        style={{
          justifyContent: 'center',
          flex: TELEMETRY_OPERATIONAL_STATE_COLUMN_SIZE,
          ...styles.smallTableCell,
        }}
      >
        {state.operationalState}
      </StyledCell>
      <StyledCell
        style={{
          justifyContent: 'center',
          flex: TELEMETRY_SPEED_COLUMN_SIZE,
          maxWidth: TELEMETRY_SPEED_COLUMN_MAX_WIDTH,
          whiteSpace: 'nowrap',
          ...styles.smallTableCell,
        }}
      >
        {state.position_speed} Km/h
      </StyledCell>
      <StyledCell
        style={{
          justifyContent: 'center',
          flex: TELEMETRY_RPM_COLUMN_SIZE,
          ...styles.smallTableCell,
        }}
      >
        {state.can_engine_rpm}
      </StyledCell>
      <StyledCell style={{ flex: TELEMETRY_ADDRESS_COLUMN_SIZE, ...styles.smallTableCell }}>
        <StyledLink
          href={`https://maps.google.com/?q=${state.position_latitude},${state.position_longitude}`}
          target="_blank"
          style={{ ...theme.typography.ParagraphXSmall }}
        >
          <EllipsedText
            inline
            breakAll
          >
            {state.geoReference}
          </EllipsedText>
        </StyledLink>
      </StyledCell>
    </StyledRow>
  );
}

export default memo(TelemetryReportRow, areEqual);
